import { useState, useCallback } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SaveIcon from '@mui/icons-material/Save';
import { useParams } from '@tanstack/react-router';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useSeriesById,
  useUpdateSeries,
  useAddBookToSeries,
  useRemoveBookFromSeries,
  type SeriesBookDto,
} from '../api/queries/series';
import { useBooks } from '../api/queries/books';
import { AppImage } from '../components/common/AppImage';
import { FilterableSelect } from '../components/common/FilterableSelect';
import MenuBookIcon from '@mui/icons-material/MenuBook';

export function SeriesDetailRoute() {
  const { seriesId } = useParams({ from: '/series/$seriesId' });
  const { data: series, isLoading, isError } = useSeriesById(seriesId);
  const updateSeries = useUpdateSeries(seriesId);

  const [name, setName] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [addBookOpen, setAddBookOpen] = useState(false);

  const effectiveName = name ?? series?.name ?? '';
  const effectiveDescription = description ?? series?.description ?? '';

  const isDirty =
    series &&
    (effectiveName !== series.name ||
      effectiveDescription !== (series.description ?? ''));

  const handleSave = async () => {
    if (!series) return;
    await updateSeries.mutateAsync({
      name: effectiveName,
      description: effectiveDescription || null,
      coverImageUrl: series.coverImageUrl,
    });
    setName(null);
    setDescription(null);
  };

  if (isLoading) return <CircularProgress />;
  if (isError || !series) {
    return <Alert severity="error">Failed to load series.</Alert>;
  }

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
        <AppImage
          src={series.coverImageUrl}
          alt={series.name}
          width={120}
          height={120}
          fallbackIcon={
            <MenuBookIcon sx={{ color: 'grey.400', fontSize: 48 }} />
          }
        />
        <Stack spacing={1} sx={{ flex: 1 }}>
          <TextField
            label="Series name"
            value={effectiveName}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
          />
          <TextField
            label="Description"
            value={effectiveDescription}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            variant="outlined"
            size="small"
          />
          {isDirty && (
            <Button
              variant="contained"
              size="small"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!effectiveName.trim() || updateSeries.isPending}
              sx={{ alignSelf: 'flex-start' }}
            >
              Save
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Book list */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Books in series
        </Typography>
        <Button variant="outlined" onClick={() => setAddBookOpen(true)}>
          Add Book
        </Button>
      </Stack>

      <SortableBookList seriesId={seriesId} books={series.books} />

      <AddBookDialog
        open={addBookOpen}
        onClose={() => setAddBookOpen(false)}
        seriesId={seriesId}
        existingBookIds={series.books.map((b) => b.bookId)}
      />
    </Paper>
  );
}

function SortableBookList({
  seriesId,
  books,
}: {
  seriesId: string;
  books: SeriesBookDto[];
}) {
  const removeBook = useRemoveBookFromSeries(seriesId);
  const [items, setItems] = useState<SeriesBookDto[]>([]);
  const displayItems = items.length > 0 ? items : books;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const currentItems = items.length > 0 ? items : books;
      const oldIndex = currentItems.findIndex((b) => b.id === active.id);
      const newIndex = currentItems.findIndex((b) => b.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      setItems(arrayMove(currentItems, oldIndex, newIndex));
    },
    [items, books],
  );

  if (displayItems.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No books in this series yet. Click "Add Book" to get started.
      </Typography>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={displayItems.map((b) => b.id)}
        strategy={verticalListSortingStrategy}
      >
        <Stack spacing={1}>
          {displayItems.map((book, index) => (
            <SortableBookItem
              key={book.id}
              book={book}
              index={index}
              onRemove={() => removeBook.mutate(book.bookId)}
              removing={removeBook.isPending}
            />
          ))}
        </Stack>
      </SortableContext>
    </DndContext>
  );
}

function SortableBookItem({
  book,
  index,
  onRemove,
  removing,
}: {
  book: SeriesBookDto;
  index: number;
  onRemove: () => void;
  removing: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: book.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card ref={setNodeRef} style={style} variant="outlined">
      <CardContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 1.5,
          '&:last-child': { pb: 1.5 },
        }}
      >
        <IconButton
          size="small"
          {...attributes}
          {...listeners}
          sx={{ cursor: 'grab' }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Chip label={index + 1} size="small" variant="outlined" />
        <Typography variant="body1" sx={{ flex: 1 }}>
          {book.bookTitle}
        </Typography>
        <IconButton
          size="small"
          onClick={onRemove}
          disabled={removing}
          color="error"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </CardContent>
    </Card>
  );
}

function AddBookDialog({
  open,
  onClose,
  seriesId,
  existingBookIds,
}: {
  open: boolean;
  onClose: () => void;
  seriesId: string;
  existingBookIds: string[];
}) {
  const { data: allBooks } = useBooks();
  const addBook = useAddBookToSeries(seriesId);
  const [selectedBookId, setSelectedBookId] = useState('');

  const availableBooks = (allBooks ?? []).filter(
    (b) => !existingBookIds.includes(b.id),
  );

  const handleSubmit = async () => {
    if (!selectedBookId) return;
    await addBook.mutateAsync(selectedBookId);
    setSelectedBookId('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Book to Series</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {availableBooks.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              All your books are already in this series, or you have no books
              yet.
            </Typography>
          ) : (
            <FilterableSelect
              label="Select a book"
              value={selectedBookId}
              onChange={(newValue) => setSelectedBookId(newValue)}
              allowEmpty={true}
              emptyLabel="-- Select --"
              options={availableBooks.map((b) => ({
                value: b.id,
                label: b.title,
              }))}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedBookId || addBook.isPending}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
